import type {
  ProviderContentProps,
  UIElementContentSource
} from '@smart-tv/hooks/use-ui-element-content/use-ui-element-content';

export interface UpdateStartoverDataStream {
  title: string;
  subtitle: string;
  updateStartoverNextInSecs?: number;
}

export class NextEpisode {
  mediaId?: number;
  sid?: number;
  eventId?: number;
  showNextButtonAt?: number;
  numberOfSeasons?: number;
  numberOfEpisodes?: number;
  seriesTitle?: string;
  episodeNumber?: number;
  seasonNumber?: number;
  title?: string;
  shortDescription?: string;
  longDescription?: string;
  trackingCategory?: string;

  constructor(
    mediaId?: number,
    sid?: number,
    eventId?: number,
    showNextButtonAt?: number,
    numberOfSeasons?: number,
    numberOfEpisodes?: number,
    seriesTitle?: string,
    episodeNumber?: number,
    seasonNumber?: number,
    title?: string,
    shortDescription?: string,
    longDescription?: string,
    trackingCategory?: string
  ) {
    this.mediaId = mediaId;
    this.sid = sid;
    this.eventId = eventId;
    this.showNextButtonAt = showNextButtonAt;
    this.numberOfSeasons = numberOfSeasons;
    this.numberOfEpisodes = numberOfEpisodes;
    this.seriesTitle = seriesTitle;
    this.episodeNumber = episodeNumber;
    this.seasonNumber = seasonNumber;
    this.title = title;
    this.shortDescription = shortDescription;
    this.longDescription = longDescription;
    this.trackingCategory = trackingCategory;
  }

  get streamType(): StreamType {
    if (this.eventId) {
      return StreamType.CatchUp;
    } else if (this.sid) {
      return StreamType.LiveStream;
    } else if (this.mediaId) {
      return StreamType.Vod;
    }

    return StreamType.CatchUp; // This case should never happen, but we need to return something.
  }
}

export enum StreamType {
  LiveStream = 'LIVE_STREAM',
  StartOver = 'START_OVER',
  CatchUp = 'CATCH_UP',
  Vod = 'VOD'
}

export enum ContentLayout {
  Portrait = 'PORTRAIT',
  Landscape = 'LANDSCAPE',
  Mixed = 'MIXED',
  WideLandscape = 'WIDE_LANDSCAPE',
  ProviderBox = 'PROVIDER_BOX'
}

export enum ContentListProviders {
  All = 'ALL',
  Subscribed = 'SUBSCRIBED'
}

export enum ContentType {
  Program = 'PROGRAM',
  Movies = 'MOVIES',
  Series = 'SERIES',
  Channel = 'CHANNEL',
  Provider = 'PROVIDER'
}

export enum ContentCover {
  Portrait = 'PORTRAIT',
  Landscape = 'LANDSCAPE',
  Square = 'SQUARE',
  ProviderBox = 'PROVIDER_BOX'
}

/**
 * UI Elements
 */

export enum UIElementStyle {
  ProgramChannel = 'PROGRAM_CHANNEL',
  ProgramLandscape = 'PROGRAM_LANDSCAPE',
  ProgramPortrait = 'PROGRAM_PORTRAIT',
  Provider = 'PROVIDER',
  UniverseMixed = 'UNIVERSE_MIXED',
  UniverseMovies = 'UNIVERSE_MOVIES',
  Mixed = 'MIXED',
  Portrait = 'PORTRAIT'
}

export interface UIElement {
  id: number;
  title: string;
  style: UIElementStyle;
  contentSource: UIElementContentSource;
  providerContentProps?: ProviderContentProps;
  subGenreId?: number;
  disablePagination?: boolean;
  primaryAction?: {
    actionName: string;
    actionParameters: {
      parameterName: string;
      parameterValue: string | number;
    }[];
  };
  properties?: {
    update_on_favorite?: boolean;
  };
}

export interface UIElementContent {
  id: number;
  title: string;
  layout: ContentLayout;
  contentTotalCount?: number;
  primaryAction?: {
    actionName: string;
    actionParameters: {
      parameterName: string;
      parameterValue: string | number;
    }[];
  };
  updateNext: number;
  properties: {
    showContentTitle?: boolean;
    showStripeTitle?: boolean;
    showProgress?: boolean;
    showLogo?: boolean;
    showStartTime?: boolean;
  };
  items: ContentItem[];
  showAllElementCell: { show: boolean; position: number };
}

/** Content types */
export interface ContentItem {
  accessLogo?: 'rent' | 'subscription';
  eventId?: number;
  category?: string;
  contentId?: number;
  contentType: ContentType;
  contentLayout: ContentLayout;
  cover: string;
  coverType: ContentCover;
  duration: number;
  endTime?: Date;
  genre?: string[];
  heroImage?: string;
  favorited?: boolean;
  logo?: string;
  position?: number;
  providerCollectionDefaultUniverse?: number;
  providerCollectionId?: number;
  providerCollectionName?: string;
  providerCount?: number;
  key: string;
  recording?: boolean;
  removable?: boolean;
  sid?: number;
  startTime?: Date;
  title?: string;
  year?: number;
}

export enum FeatureIntroLocation {
  FRONTPAGE = 'FRONTPAGE',
  PLAYER = 'PLAYER',
  LIVE_PLAYER = 'LIVE_PLAYER',
  VOD_PLAYER = 'VOD_PLAYER',
  SFF_PLAYER = 'SFF_PLAYER'
}

export interface FeatureIntro {
  id: string;
  name: string;
  locationToShowIntro: FeatureIntroLocation;
  style: string;
  steps: {
    id: string;
    headline: string;
    subHeadline?: string;
    description: string;
    link?: { text: string; url: string };
    media?: { mediaType: string; mediaUrl: string };
  }[];
}

export type AvailableFeatureIntros = FeatureIntro[];

export interface FeatureFlags {
  showSearch: boolean;
  showUniverses: boolean;
  showMyDetails: boolean;
}

export interface Universe {
  id: number;
  title: string;
  href?: string;
  providers?: ContentListProviders;
}

export enum UniverseSortingValue {
  VIEWS = 'VIEWS',
  NEWEST = 'NEWEST'
}

export interface LiveTvProgram {
  category: string;
  duration: number;
  endTime: Date;
  eventId: number;
  genres?: string[];
  image: string;
  npvrTrickmodes: number;
  recordable: boolean;
  recording: boolean;
  sid: number;
  startTime: Date;
  title?: string;
  year?: number;
}

export interface LiveTvChannel {
  currentProgram?: LiveTvProgram;
  icon: string;
  name: string;
  nextProgram?: LiveTvProgram;
  sid: number;
}

export type GetContentInfoParams = {
  contentType?: ContentType;
  contentId?: number;
  sid?: number;
  eventId?: number;
  universeId?: number;
  providers?: ContentListProviders;
  subProviderId?: string;
  providerCollectionId?: number;
  fetchReady: boolean;
};
